import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarDays, FlaskConical, Stethoscope } from 'lucide-react';
import { Button } from '../components/Button';
import { StatusMarker } from '../components/StatusMarker';
import { usePlatform } from '../contexts/PlatformContext';
import { labReport } from '../data/biomarkers';
import { OrderStatusBadge } from '../components/OrderStatusBadge';
import { GradeBadge } from '../components/GradeBadge';
import { gradeFor, panelScore } from '../utils/grade';
import { getStatus } from '../utils/biomarkers';
import { formatOrderDate } from '../utils/orders';
import { formatAED } from '../utils/pack';
export function Profile() {
  const {
    user,
    goal,
    stage,
    panel,
    orders
  } = usePlatform();
  const navigate = useNavigate();
  if (!user || !goal) return <Navigate to="/" replace data-id="element-333" data-mp="2bizozmeobdsp" />;
  const flagged = panel.filter(b => getStatus(b) !== 'optimal').length;
  const initials = user.name.split(' ').map(n => n[0]).join('').slice(0, 2);
  const details = [{
    label: 'Age',
    value: `${user.age}`
  }, {
    label: 'Location',
    value: user.location
  }, {
    label: 'Height',
    value: `${user.heightCm} cm`
  }, {
    label: 'Weight',
    value: `${user.weightKg} kg`
  }, {
    label: 'Blood type',
    value: user.bloodType
  }, {
    label: 'Member since',
    value: user.memberSince
  }];
  return <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-id="element-334" data-mp="2f73zebkkyeun">
      <section aria-labelledby="profile-heading" className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 md:p-8" data-id="element-335" data-mp="2t37pptsc231u">
        <div className="flex flex-wrap items-center gap-5" data-id="element-336" data-mp="2p0umbc126h88">
          <span className="flex size-16 items-center justify-center rounded-full border border-[#9b8cf0]/50 bg-[#9b8cf0]/10 text-xl font-medium text-[#c4baff]" data-id="element-337" data-mp="32tajvxb6xn1r">
            {initials}
          </span>
          <div className="flex-1" data-id="element-338" data-mp="3ctkurb9r6idr">
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-339" data-mp="3cqf9660c1yms">Your profile</p>
            <h1 id="profile-heading" className="mt-1 text-2xl font-medium tracking-tight md:text-3xl" data-id="element-340" data-mp="37rlu495edzki">{user.name}</h1>
            <p className="text-sm text-muted-foreground" data-id="element-341" data-mp="3jvgbq6uxw2y6">{user.email}</p>
          </div>
          <StatusMarker goal={goal} stage={stage} data-id="element-342" data-mp="3ousykrlt9a0r" />
        </div>

        <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-3" data-id="element-343" data-mp="3othvku06w6ap">
          {details.map(d => <div key={d.label} className="bg-[#0f1017] p-4" data-id="element-344" data-mp="2fzwjdz9jzrr">
              <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-id="element-345" data-mp="6gtgxxdhywfw">{d.label}</dt>
              <dd className="mt-1 font-medium" data-id="element-346" data-mp="bg65cmk0obkl">{d.value}</dd>
            </div>)}
        </dl>

        <div className="mt-8 rounded-2xl border border-white/10 p-5" data-id="element-347" data-mp="gfj15wsv8szb">
          <div className="flex items-center justify-between gap-4" data-id="element-348" data-mp="gcn3m9kxk6be">
            <div data-id="element-349" data-mp="2d8u8yx0kp10w">
              <p className="font-medium" data-id="element-350" data-mp="2d9dtmlhly3im">Health status</p>
              <p className="text-sm text-muted-foreground" data-id="element-351" data-mp="2i7far0vh3srg">
                {goal === 'pregnancy' ? 'Your results and packs follow your pregnancy stage.' : 'Your results and packs focus on long-term wellbeing.'}
              </p>
            </div>
            <StatusMarker goal={goal} stage={stage} size="sm" data-id="element-352" data-mp="20wihbrsqpfay" />
          </div>
        </div>
      </section>

      <div className="space-y-6" data-id="element-353" data-mp="1vyqei2qk48rz">
        <section className="rounded-3xl border border-[#9b8cf0]/40 bg-[#9b8cf0]/[0.05] p-6 md:p-8" data-id="element-354" data-mp="1vzs194gxqjpj">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#c4baff]" data-id="element-355" data-mp="2tkxsg38r3dh0">
            <FlaskConical className="size-3.5" data-id="element-356" data-mp="2okjiugri3vgv" /> Latest blood test
          </p>
          <div className="mt-4 flex items-center justify-between gap-4" data-id="element-357" data-mp="2jlyjejk2pfth">
            <div data-id="element-358" data-mp="2ep85o2vqqwb8">
              <p className="text-4xl font-medium tracking-tight" data-id="element-359" data-mp="2ennaqc5ilbcu">{panel.length}</p>
              <p className="text-muted-foreground" data-id="element-360" data-mp="189fj70rz76l8">biomarkers analysed</p>
            </div>
            <div className="text-right" data-id="element-361" data-mp="139070cjfk54u">
              <GradeBadge grade={gradeFor(panelScore(panel))} size="lg" data-id="element-362" data-mp="137ynrks9gcc1" />
              <p className="mt-1 text-xs text-muted-foreground" data-id="element-363" data-mp="2s2p49sb3dbna">{gradeFor(panelScore(panel)).label}</p>
            </div>
          </div>
          <div className="mt-5 flex gap-6 text-sm" data-id="element-364" data-mp="2wwa1vj8rw3ly">
            <p data-id="element-365" data-mp="2i6ccx130r9vp"><span className="font-medium text-[#8fd3b0]" data-id="element-366" data-mp="2i6cayvjgkzt2">{panel.length - flagged}</span> in range</p>
            <p data-id="element-367" data-mp="2n5ff8drm71zz"><span className="font-medium text-[#f5b454]" data-id="element-368" data-mp="2n55r6vq4r4wx">{flagged}</span> need attention</p>
          </div>
          <p className="mt-5 flex items-center gap-2 text-sm text-muted-foreground" data-id="element-369" data-mp="3av9qiqwacii4">
            <CalendarDays className="size-4" data-id="element-370" data-mp="3fs09pn3dzu7r" /> {labReport.lab} · {labReport.date}
          </p>
          <Button size="lg" className="mt-6 w-full bg-[#9b8cf0] text-[#0f1017] hover:bg-[#b0a4f5]" onClick={() => navigate('/results')} data-id="element-371" data-mp="30ur2djxoufue">
            View my results <ArrowRight className="size-4" data-id="element-372" data-mp="1pfkhw6xowr9y" />
          </Button>
        </section>

        <section className="flex items-center gap-4 rounded-3xl border border-white/10 bg-white/[0.02] p-6" data-id="element-373" data-mp="1ug8auy5hbi6t">
          <span className="flex size-11 items-center justify-center rounded-full border border-white/15" data-id="element-374" data-mp="chakncg0mkpt">
            <Stethoscope className="size-5 text-[#c4baff]" data-id="element-375" data-mp="7hxou27623b3" />
          </span>
          <div data-id="element-376" data-mp="lja3maalbyve">
            <p className="font-medium" data-id="element-377" data-mp="lhqlsf9adfri">{labReport.reviewer}</p>
            <p className="text-sm text-muted-foreground" data-id="element-378" data-mp="hh6onbopqc5d">Your family-medicine doctor · verifies every pack</p>
          </div>
        </section>

        <section aria-labelledby="recent-orders" className="rounded-3xl border border-white/10 bg-white/[0.02] p-6" data-id="element-379" data-mp="3py1f1robq42k">
          <div className="flex items-center justify-between" data-id="element-380" data-mp="3kzge1q18zgd2">
            <h2 id="recent-orders" className="font-medium" data-id="element-381" data-mp="2k5vd3qvi94s">Recent orders</h2>
            <Link to="/orders" className="text-sm text-[#c4baff] hover:underline" data-id="element-382" data-mp="3tytskx48av4n">View all</Link>
          </div>
          <ul className="mt-4 divide-y divide-white/5" data-id="element-383" data-mp="368haitf9ri0c">
            {orders.slice(0, 3).map(o => <li key={o.id} data-id="element-384" data-mp="327xc7miym8cm">
                <Link to={`/orders/${o.id}`} className="flex items-center justify-between gap-3 py-3 hover:opacity-80" data-id="element-385" data-mp="326w3hopf1xy2">
                  <span data-id="element-386" data-mp="154ox56isfb2y">
                    <span className="block text-sm" data-id="element-387" data-mp="1a5un4wl2ck8s">{o.label}</span>
                    <span className="font-mono text-[11px] text-muted-foreground" data-id="element-388" data-mp="1a41excusxryo">{formatOrderDate(o.placedAt)} · {formatAED(o.total)}</span>
                  </span>
                  <OrderStatusBadge status={o.status} data-id="element-389" data-mp="1jumf34q4j4jp" />
                </Link>
              </li>)}
          </ul>
        </section>
      </div>
    </div>;
}